/**
 * Validation utilities for order items
 */

/**
 * Validate a single order item
 * @param {Object} item - The order item to validate
 * @returns {Object} - Object with field errors { field: errorMessage }
 */
export const validateOrderItem = (item) => {
	const errors = {};

	// Validate quantity
	if (item.quantity === undefined || item.quantity === null) {
		errors.quantity = 'Quantity is required';
	} else if (item.quantity < 0) {
		errors.quantity = 'Quantity cannot be negative';
	} else if (item.quantity === 0 && item.status !== 'cancelled') {
		errors.quantity = 'Quantity must be greater than 0 for non-cancelled items';
	} else if (!Number.isInteger(item.quantity)) {
		errors.quantity = 'Quantity must be a whole number';
	}

	// Validate item_price
	if (item.item_price === undefined || item.item_price === null) {
		errors.item_price = 'Price is required';
	} else if (item.item_price < 0) {
		errors.item_price = 'Price cannot be negative';
	} else if (isNaN(item.item_price)) {
		errors.item_price = 'Price must be a valid number';
	}

	// Validate coupon_discount_amount
	if (item.coupon_discount_amount !== undefined && item.coupon_discount_amount !== null) {
		if (item.coupon_discount_amount < 0) {
			errors.coupon_discount_amount = 'Discount cannot be negative';
		} else if (isNaN(item.coupon_discount_amount)) {
			errors.coupon_discount_amount = 'Discount must be a valid number';
		}

		// Check if discount exceeds item total
		const itemTotal = item.quantity * item.item_price;
		if (item.coupon_discount_amount > itemTotal) {
			errors.coupon_discount_amount = `Discount cannot exceed item total ($${itemTotal.toFixed(2)})`;
		}
	}

	// Validate status
	const validStatuses = ['new', 'picking', 'picked', 'reserved', 'sorted', 'cancelled', 'change_requested'];
	if (!item.status) {
		errors.status = 'Status is required';
	} else if (!validStatuses.includes(item.status)) {
		errors.status = 'Invalid status selected';
	}

	// Validate cancel reason if item is cancelled
	if (item.status === 'cancelled' && !item.cancelReason) {
		errors.cancelReason = 'Cancel reason is required for cancelled items';
	}

	// Validate item_id
	if (!item.item_id) {
		errors.item_id = 'Item ID is missing';
	}

	// Validate order_item_id for existing items
	if (!item.order_item_id && item.id !== undefined) {
		errors.order_item_id = 'Order item ID is missing';
	}

	return errors;
};

/**
 * Validate all order items
 * @param {Array} items - Array of order items
 * @returns {Object} - Object with item errors { itemId: { field: errorMessage } }
 */
export const validateOrderItems = (items) => {
	const itemErrors = {};
	let hasErrors = false;

	items.forEach((item) => {
		const errors = validateOrderItem(item);
		if (Object.keys(errors).length > 0) {
			itemErrors[item.id] = errors;
			hasErrors = true;
		}
	});

	return {itemErrors, hasErrors};
};

/**
 * Get summary of validation errors
 * @param {Object} itemErrors - Object with item errors { itemId: { field: errorMessage } }
 * @returns {Array} - Array of error messages
 */
export const getValidationSummary = (itemErrors) => {
	const summary = [];

	Object.entries(itemErrors).forEach(([itemId, errors]) => {
		Object.entries(errors).forEach(([field, message]) => {
			summary.push({
				itemId: parseInt(itemId),
				field,
				message,
			});
		});
	});

	return summary;
};

/**
 * Format validation errors for display
 * @param {Object} itemErrors - Object with item errors
 * @param {Array} items - Array of order items to get item names
 * @returns {string} - Formatted error message
 */
export const formatValidationErrors = (itemErrors, items) => {
	const summary = getValidationSummary(itemErrors);

	if (summary.length === 0) {
		return '';
	}

	if (summary.length === 1) {
		const error = summary[0];
		const item = items.find(i => i.id === error.itemId);
		const itemName = item ? item.item_name : `Item #${error.itemId}`;
		return `${itemName}: ${error.message}`;
	}

	// Multiple errors
	const errorCount = summary.length;
	const itemCount = Object.keys(itemErrors).length;
	return `Found ${errorCount} validation error${errorCount > 1 ? 's' : ''} in ${itemCount} item${itemCount > 1 ? 's' : ''}. Please check highlighted fields.`;
};

/**
 * Check if item has any validation errors
 * @param {Object} itemErrors - All item errors
 * @param {number} itemId - ID of item to check
 * @returns {boolean} - True if item has errors
 */
export const itemHasErrors = (itemErrors, itemId) => {
	return itemErrors && itemErrors[itemId] && Object.keys(itemErrors[itemId]).length > 0;
};

/**
 * Get error message for a specific field
 * @param {Object} itemErrors - All item errors
 * @param {number} itemId - ID of item
 * @param {string} field - Field name
 * @returns {string} - Error message or empty string
 */
export const getFieldError = (itemErrors, itemId, field) => {
	return itemErrors?.[itemId]?.[field] || '';
};
